import { Component } from 'react'
import type { ChangeEventHandler, ComponentType, MouseEvent as RMEvent, ReactNode } from 'react'
import { Box, CircularProgress, Divider, Fade, Grid, Stack, TablePagination, Typography } from '@mui/material'
import { mergeObjects } from '../utils'
import { defaultPagination } from './helpers'
import { CreateTableGridItemActions } from './item-actions'
import { GlobalModal, PopoverGlobal } from '../api-context'
import { CreateFilterBar, IFilterState } from './filter-bar'
import { ItemDefault, tableGridClasses, TableGridStyled } from './styled'
import { ITableGridFilter, IPaginationModel, ITableGridParams, ITableGridProps, ITableGridInnerSlots } from './types'
import ToolbarPannelDefault from './toolbar-pannel'

function CreateTableGrid<T extends object>(params: ITableGridParams<T>): ComponentType<ITableGridProps<T>> {
  const FilterBarInstance = CreateFilterBar<T>(params.filterBarConfigs ?? { fields: {} })
  const ItemActions = CreateTableGridItemActions<T>()

  class TableGrid extends Component<ITableGridProps<T>> {
    private filterStateStore: ITableGridFilter<T> = { pagination: defaultPagination }
    get filterState(): ITableGridFilter<T> {
      return this.props.filter ?? this.filterStateStore
    }

    get slots(): ITableGridInnerSlots<T> {
      return {
        ...Object.assign({}, params.slots, this.props.slots),
        TableGridItem: this.props.slots?.item ?? params.item ?? (() => <ItemDefault />),
        ToolbarPannel: this.props.slots?.toolbar ?? params.slots?.toolbar ?? ToolbarPannelDefault
      }
    }

    get mergeConfig() {
      return {
        size: params.size ?? { xs: 12, sm: 6, md: 4 }
      }
    }

    setFilterState = (value: Partial<ITableGridFilter<T>>) => {
      try {
        if (!!value.details) {
          this.filterStateStore = { pagination: this.filterStateStore.pagination, ...value }
        } else {
          this.filterStateStore = mergeObjects<any>({}, this.filterState, value)
        }
        this.props.onFilterChange && this.props.onFilterChange(this.filterStateStore)
        this.forceUpdate()
      } catch (error) {
        console.log('Set Filter State error:', error)
      }
    }

    render() {
      const { TableGridItem, ToolbarPannel, actionProps } = this.slots
      const items = this.getItems()
      const pagination = this.getPagination()
      return this.preRender(
        <TableGridStyled className={this.getRootClasses()}>
          <ToolbarPannel title={params.title} {...this.slots.toolbarProps} />
          <Stack className={tableGridClasses.filterBarWrap} sx={this.slots.filterBar?.sx}>
            {this.slots.filterBar?.before || null}
            <FilterBarInstance value={this.filterState} onChange={this.handleFilterChange} />
            {this.slots.filterBar?.after || null}
          </Stack>
          <div className={tableGridClasses.content}>
            <div className={[tableGridClasses.contentInner, tableGridClasses.contentPosition].join(' ')}>
              <Grid container spacing={2} {...this.slots.gridContainer}>
                {items.map((item, index) => (
                  <Grid key={params.getSelecterId(item).toString() + index} item {...this.mergeConfig.size} {...this.slots.gridItem}>
                    <div className={tableGridClasses.item}>
                      <TableGridItem value={item} />
                      <Box className={tableGridClasses.actions}>
                        <ItemActions value={item} {...actionProps} />
                      </Box>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </div>
            <Fade in={this.filterState.loading} unmountOnExit>
              <div className={[tableGridClasses.overlay, tableGridClasses.contentPosition].join(' ')}>
                <CircularProgress size={36} />
              </div>
            </Fade>
            <Fade in={items.length < 1 && !this.filterState.loading} unmountOnExit>
              <div className={[tableGridClasses.noItems, tableGridClasses.contentPosition].join(' ')}>
                <Typography variant='body2'>No items data</Typography>
              </div>
            </Fade>
          </div>
          <Divider sx={{ position: 'relative', zIndex: 2 }} />
          <TablePagination
            component='div'
            count={this.props.data.totalItems ?? 0}
            page={pagination.page}
            onPageChange={this.handleChangePage}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            slotProps={{ select: { MenuProps: { disableScrollLock: true } } }}
            {...this.slots.tablePaginationProps}
          />
        </TableGridStyled>
      )
    }

    preRender = (children?: ReactNode) => (
      <GlobalModal>
        <PopoverGlobal.Provider />
        {children}
      </GlobalModal>
    )

    getPagination = (): IPaginationModel => this.filterState.pagination ?? defaultPagination

    getRootClasses = () => {
      const classes = [tableGridClasses.root]
      if (this.filterState.loading) classes.push(tableGridClasses.loading)
      return classes.join(' ')
    }

    getItems = () => {
      return this.props.data.items ?? []
    }

    handleFilterChange = (filterStateChange: IFilterState<T>) => {
      this.setFilterState(filterStateChange)
    }

    handleChangePage = (_: RMEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
      const pagination = this.getPagination()
      this.setFilterState({ pagination: { page, pageSize: pagination.pageSize } })
    }

    handleChangeRowsPerPage: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
      this.setFilterState({ pagination: { page: 0, pageSize: parseInt(event.target.value, 10) } })
    }
  }

  return TableGrid
}

export default CreateTableGrid
